// ============================================================================
// SERVICE WORKFLOW MANAGER
// Orchestrates the complete field service workflow:
// Ticket → Job → Appointment → Route → Field Work → Completion → Billing
// Provides unified interface for workflow transitions and management
// Date: December 18, 2025
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/support-management/ticket-lifecycle.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/support-management/ticket-lifecycle)
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Ticket,
  Briefcase,
  Calendar,
  Route,
  CheckCircle,
  DollarSign,
  ArrowRight,
  Plus,
  Link2,
  AlertCircle,
  Clock,
  MapPin,
  User,
  Wrench,
  Truck,
  Building2,
  Package,
  ChevronRight,
  X,
  Loader2,
  Play,
  FileText,
  Search,
  Server,
  Thermometer,
  Zap
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { notify, confirm } from '@/lib/notifications';
import UserDocsLink from '@/components/shared/UserDocsLink';
import type { WorkItem } from '../UniversalWorkItems';

// ============================================================================
// TYPES
// ============================================================================

export type WorkflowStage = 
  | 'ticket'        // Support ticket received
  | 'job'           // Job created/converted
  | 'scheduled'     // Appointment scheduled
  | 'dispatched'    // Assigned to route/technician
  | 'in_progress'   // Field work active
  | 'completed'     // Work done
  | 'billing'       // Ready for invoicing
  | 'closed';       // Fully complete

export interface WorkflowTransition {
  from: WorkflowStage;
  to: WorkflowStage;
  action: string;
  label: string;
  icon: React.ElementType;
  color: string;
  requiresData?: string[];
}

export interface WorkflowContext {
  ticket?: WorkItem;
  job?: WorkItem;
  appointments?: WorkItem[];
  route?: WorkItem;
  assets?: WorkItem[];
  invoice?: WorkItem;
}

interface ServiceWorkflowManagerProps {
  sourceItem: WorkItem;
  context?: WorkflowContext;
  onTransition?: (newItem: WorkItem, action: string) => void;
  onClose?: () => void;
  compact?: boolean;
}

// ============================================================================
// WORKFLOW CONFIGURATION
// ============================================================================

const WORKFLOW_STAGES: Record<WorkflowStage, {
  label: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  description: string;
}> = {
  ticket: {
    label: 'Support Ticket',
    icon: Ticket,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    description: 'Customer reported issue'
  },
  job: {
    label: 'Service Job',
    icon: Briefcase,
    color: 'text-green-600',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    description: 'Work order created'
  },
  scheduled: {
    label: 'Scheduled',
    icon: Calendar,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    description: 'Appointment booked'
  },
  dispatched: {
    label: 'Dispatched',
    icon: Route,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    description: 'Technician en route'
  },
  in_progress: {
    label: 'In Progress',
    icon: Play,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    description: 'Work being performed'
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
    description: 'Work finished'
  },
  billing: {
    label: 'Ready for Billing',
    icon: DollarSign,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/30',
    description: 'Invoice pending'
  },
  closed: {
    label: 'Closed',
    icon: CheckCircle,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100 dark:bg-gray-900/30',
    description: 'Fully resolved'
  }
};

const WORKFLOW_TRANSITIONS: WorkflowTransition[] = [
  {
    from: 'ticket',
    to: 'job',
    action: 'convertToJob',
    label: 'Convert to Job',
    icon: Briefcase,
    color: 'bg-green-600 hover:bg-green-700'
  },
  {
    from: 'job',
    to: 'scheduled',
    action: 'scheduleAppointment',
    label: 'Schedule Appointment',
    icon: Calendar,
    color: 'bg-purple-600 hover:bg-purple-700',
    requiresData: ['scheduled_start', 'assigned_to']
  },
  {
    from: 'scheduled',
    to: 'dispatched',
    action: 'dispatch',
    label: 'Dispatch Technician',
    icon: Route,
    color: 'bg-orange-600 hover:bg-orange-700',
    requiresData: ['assigned_to']
  },
  {
    from: 'dispatched',
    to: 'in_progress',
    action: 'startWork',
    label: 'Start Work',
    icon: Play,
    color: 'bg-yellow-600 hover:bg-yellow-700'
  },
  {
    from: 'in_progress',
    to: 'completed',
    action: 'completeWork',
    label: 'Complete Work',
    icon: CheckCircle,
    color: 'bg-emerald-600 hover:bg-emerald-700'
  },
  {
    from: 'completed',
    to: 'billing',
    action: 'readyForBilling',
    label: 'Ready for Billing',
    icon: DollarSign,
    color: 'bg-cyan-600 hover:bg-cyan-700'
  },
  {
    from: 'completed',
    to: 'closed',
    action: 'approveAndClose',
    label: 'Approve & Close',
    icon: CheckCircle,
    color: 'bg-emerald-600 hover:bg-emerald-700'
  },
  {
    from: 'billing',
    to: 'closed',
    action: 'close',
    label: 'Close Job',
    icon: CheckCircle,
    color: 'bg-gray-600 hover:bg-gray-700'
  }
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getWorkflowStage(item: WorkItem): WorkflowStage {
  if (item.item_type === 'ticket') {
    if (item.status === 'closed' || item.status === 'resolved') return 'closed';
    return 'ticket';
  }
  
  if (item.item_type === 'job') {
    switch (item.status) {
      case 'draft':
      case 'pending':
      case 'new':
        return 'job';
      case 'scheduled':
      case 'confirmed':
        return 'scheduled';
      case 'dispatched':
      case 'assigned':
      case 'en_route':
        return 'dispatched';
      case 'in_progress':
      case 'on_site':
      case 'working':
        return 'in_progress';
      case 'completed':
      case 'done':
        return 'completed';
      case 'resolved':
        return 'completed'; // Resolved = field work done, awaiting admin closure
      case 'pending_billing':
      case 'to_invoice':
        return 'billing';
      case 'closed':
      case 'invoiced':
        return 'closed';
      default:
        return 'job';
    }
  }
  
  if (item.item_type === 'appointment') {
    if (item.status === 'completed' || item.status === 'resolved') return 'completed';
    if (item.status === 'in_progress') return 'in_progress';
    if (item.status === 'on_route') return 'dispatched';
    if (item.status === 'on_site') return 'in_progress';
    return 'scheduled';
  }
  
  return 'job';
}

function getAvailableTransitions(stage: WorkflowStage): WorkflowTransition[] {
  return WORKFLOW_TRANSITIONS.filter(t => t.from === stage);
}

// ============================================================================
// ASSET PICKER COMPONENT
// ============================================================================

interface Asset {
  id: string;
  item_number: string;
  title: string;
  item_subtype?: string;
  location_address?: string;
  status?: string;
}

interface AssetPickerProps {
  selectedAssets: Asset[];
  onSelect: (assets: Asset[]) => void;
  isDark: boolean;
}

function AssetPicker({ selectedAssets, onSelect, isDark }: AssetPickerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Load assets from API
  const loadAssets = useCallback(async (query: string = '') => {
    setLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const params = new URLSearchParams({
        item_type: 'asset',
        status: 'active',
        limit: '20'
      });
      
      if (query) {
        params.append('search', query);
      }

      const res = await fetch(`/api/v1/work-items?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      if (data.success && data.data) {
        setAssets(data.data);
      }
    } catch (error) {
      console.error('Error loading assets:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showDropdown && assets.length === 0) {
      loadAssets();
    }
  }, [showDropdown, assets.length, loadAssets]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      loadAssets(query);
    }
  };

  const toggleAsset = (asset: Asset) => {
    const isSelected = selectedAssets.some(a => a.id === asset.id);
    if (isSelected) {
      onSelect(selectedAssets.filter(a => a.id !== asset.id));
    } else {
      onSelect([...selectedAssets, asset]);
    }
  };

  const getAssetIcon = (subtype?: string) => {
    switch (subtype) {
      case 'hvac':
      case 'air_conditioning':
        return Thermometer;
      case 'electrical':
      case 'power':
        return Zap;
      case 'plant_room':
      case 'boiler':
        return Server;
      default:
        return Building2;
    }
  };

  return (
    <div className="space-y-2">
      <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
        Link to Asset (optional)
      </label>
      
      {/* Selected Assets */}
      {selectedAssets.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedAssets.map(asset => {
            const Icon = getAssetIcon(asset.item_subtype);
            return (
              <div
                key={asset.id}
                className={`flex items-center gap-2 px-2 py-1 rounded-lg text-sm ${
                  isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'
                }`}
              >
                <Icon className="w-3 h-3" />
                <span>{asset.title}</span>
                <button
                  onClick={() => toggleAsset(asset)}
                  className="hover:text-red-500"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => setShowDropdown(true)}
              placeholder="Search assets (HVAC, plant rooms, boilers...)"
              className={`w-full pl-9 pr-3 py-2 rounded-lg border ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>
          {showDropdown && (
            <button
              onClick={() => setShowDropdown(false)}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <div className={`absolute z-10 w-full mt-1 rounded-lg border shadow-lg max-h-60 overflow-y-auto ${
            isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
          }`}>
            {loading ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : assets.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                No assets found
              </div>
            ) : (
              assets.map(asset => {
                const Icon = getAssetIcon(asset.item_subtype);
                const isSelected = selectedAssets.some(a => a.id === asset.id);
                return (
                  <button
                    key={asset.id}
                    onClick={() => toggleAsset(asset)}
                    className={`w-full flex items-center gap-3 p-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      isSelected ? (isDark ? 'bg-blue-900/20' : 'bg-blue-50') : ''
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-blue-500' : 'text-gray-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {asset.title}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {asset.item_number} • {asset.item_subtype?.replace(/_/g, ' ')}
                        {asset.location_address && ` • ${asset.location_address}`}
                      </p>
                    </div>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
      
      <p className="text-xs text-gray-500">
        Link this job to assets that need service (HVAC units, plant rooms, boilers, etc.)
      </p>
    </div>
  );
}

// ============================================================================
// CONVERT TO JOB MODAL
// ============================================================================

interface ConvertToJobModalProps {
  ticket: WorkItem;
  onConvert: (jobData: any) => void;
  onCancel: () => void;
  loading?: boolean;
}

function ConvertToJobModal({ ticket, onConvert, onCancel, loading }: ConvertToJobModalProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  
  const [jobData, setJobData] = useState({
    title: ticket.title,
    description: ticket.description || '',
    priority: (ticket.priority || 'medium') as 'low' | 'medium' | 'high' | 'urgent' | 'critical',
    estimated_hours: 2,
    copy_attachments: true,
    copy_comments: true,
    link_ticket: true,
    linked_assets: [] as Asset[]
  });

  const handlePriorityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setJobData({ ...jobData, priority: e.target.value as 'low' | 'medium' | 'high' | 'urgent' | 'critical' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className={`w-full max-w-lg rounded-xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-inherit z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Briefcase className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Convert Ticket to Job
                <UserDocsLink
                  docsPath="/total-operations/workflows/support-management/ticket-lifecycle#convert-a-ticket-to-a-job"
                  label="User guide: Ticket lifecycle & SLA"
                  className="ml-1 align-middle"
                />
              </h3>
              <p className="text-sm text-gray-500">Create a service job from ticket #{ticket.item_number}</p>
            </div>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Job Title */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Job Title
            </label>
            <input
              type="text"
              value={jobData.title}
              onChange={(e) => setJobData({ ...jobData, title: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg border ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:ring-2 focus:ring-green-500 focus:border-transparent`}
            />
          </div>

          {/* Priority & Estimated Hours */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Priority
              </label>
              <select
                value={jobData.priority}
                onChange={handlePriorityChange}
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Estimated Hours
              </label>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={jobData.estimated_hours}
                onChange={(e) => setJobData({ ...jobData, estimated_hours: parseFloat(e.target.value) })}
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
          </div>

          {/* Asset Picker - NEW */}
          <AssetPicker
            selectedAssets={jobData.linked_assets}
            onSelect={(assets) => setJobData({ ...jobData, linked_assets: assets })}
            isDark={isDark}
          />

          {/* Copy Options */}
          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
            <p className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Copy from ticket:
            </p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={jobData.copy_comments}
                  onChange={(e) => setJobData({ ...jobData, copy_comments: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Copy comments as job notes
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={jobData.copy_attachments}
                  onChange={(e) => setJobData({ ...jobData, copy_attachments: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Copy attachments
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={jobData.link_ticket}
                  onChange={(e) => setJobData({ ...jobData, link_ticket: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Link job to original ticket
                </span>
              </label>
            </div>
          </div>

          {/* Info about what gets copied automatically */}
          <div className={`p-3 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
            <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
              <strong>Automatically copied:</strong> Contact details, location, description, and custom fields from the ticket.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCancel}
            className={`px-4 py-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            Cancel
          </button>
          <button
            onClick={() => onConvert(jobData)}
            disabled={loading || !jobData.title?.trim()}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Briefcase className="w-4 h-4" />
            )}
            Create Job
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SCHEDULE APPOINTMENT MODAL
// ============================================================================

interface ScheduleAppointmentModalProps {
  job: WorkItem;
  technicians: Array<{ id: number; name: string; email: string }>;
  onSchedule: (appointmentData: any) => void;
  onCancel: () => void;
  loading?: boolean;
}

function ScheduleAppointmentModal({ job, technicians, onSchedule, onCancel, loading }: ScheduleAppointmentModalProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  
  // Default to tomorrow at 9am
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  
  const [appointmentData, setAppointmentData] = useState({
    scheduled_start: tomorrow.toISOString().slice(0, 16),
    estimated_hours: job.estimated_hours || 2,
    assigned_to: [] as number[],
    appointment_type: 'site_visit',
    include_travel_time: true,
    estimated_travel_minutes: 30,
    notify_customer: true
  });

  const APPOINTMENT_TYPES = [
    { value: 'site_visit', label: 'Site Visit' },
    { value: 'installation', label: 'Installation' },
    { value: 'repair', label: 'Repair' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'inspection', label: 'Inspection' },
    { value: 'follow_up', label: 'Follow-up' },
    { value: 'emergency', label: 'Emergency Call' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className={`w-full max-w-lg rounded-xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-inherit">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Schedule Appointment
              </h3>
              <p className="text-sm text-gray-500">For job #{job.item_number}</p>
            </div>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Appointment Type */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Appointment Type
            </label>
            <select
              value={appointmentData.appointment_type}
              onChange={(e) => setAppointmentData({ ...appointmentData, appointment_type: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg border ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              {APPOINTMENT_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Scheduled Start */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Scheduled Start
            </label>
            <input
              type="datetime-local"
              value={appointmentData.scheduled_start}
              onChange={(e) => setAppointmentData({ ...appointmentData, scheduled_start: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg border ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          {/* Estimated Hours */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Estimated Duration (hours)
            </label>
            <input
              type="number"
              min="0.5"
              step="0.5"
              value={appointmentData.estimated_hours}
              onChange={(e) => setAppointmentData({ ...appointmentData, estimated_hours: parseFloat(e.target.value) })}
              className={`w-full px-3 py-2 rounded-lg border ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          {/* Assign Technicians */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Assign Technician(s)
            </label>
            <div className={`max-h-48 overflow-y-auto border rounded-lg ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
              {technicians.length === 0 ? (
                <p className="p-3 text-sm text-gray-500">No technicians available</p>
              ) : (
                technicians.map(tech => (
                  <label
                    key={tech.id}
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b last:border-b-0 ${
                      isDark ? 'border-gray-700' : 'border-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={appointmentData.assigned_to.includes(tech.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAppointmentData({ 
                            ...appointmentData, 
                            assigned_to: [...appointmentData.assigned_to, tech.id] 
                          });
                        } else {
                          setAppointmentData({ 
                            ...appointmentData, 
                            assigned_to: appointmentData.assigned_to.filter(id => id !== tech.id) 
                          });
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {tech.name}
                      </p>
                      <p className="text-xs text-gray-500">{tech.email}</p>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Travel Time */}
          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={appointmentData.include_travel_time}
                onChange={(e) => setAppointmentData({ ...appointmentData, include_travel_time: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Include travel time
              </span>
            </label>
            {appointmentData.include_travel_time && (
              <div className="mt-2 flex items-center gap-2">
                <Route className="w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  min="5"
                  step="5"
                  value={appointmentData.estimated_travel_minutes}
                  onChange={(e) => setAppointmentData({ ...appointmentData, estimated_travel_minutes: parseInt(e.target.value) })}
                  className={`w-20 px-2 py-1 rounded border text-sm ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
                <span className="text-sm text-gray-500">minutes travel time</span>
              </div>
            )}
          </div>

          {/* Customer Notification */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={appointmentData.notify_customer}
              onChange={(e) => setAppointmentData({ ...appointmentData, notify_customer: e.target.checked })}
              className="rounded border-gray-300"
            />
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Notify customer of appointment
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCancel}
            className={`px-4 py-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            Cancel
          </button>
          <button
            onClick={() => onSchedule(appointmentData)}
            disabled={loading || appointmentData.assigned_to.length === 0}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Calendar className="w-4 h-4" />
            )}
            Schedule
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

// Transition rule from TransitionRulesManager
interface TransitionRule {
  id: number;
  name: string;
  from_status: string;
  to_status: string;
  item_type: string | null;
  conditions: any[];
  required_fields: any[];
  validators: any[];
  assignee_rule: string;
  notify_users: any[];
  auto_fields: Record<string, any>;
  is_active: boolean;
}

export default function ServiceWorkflowManager({
  sourceItem,
  context,
  onTransition,
  onClose,
  compact = false
}: ServiceWorkflowManagerProps) {
  const { currentTheme, clientBranding } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const primaryColor = clientBranding?.primary_color || '#3b82f6';
  
  const [loading, setLoading] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [technicians, setTechnicians] = useState<Array<{ id: number; name: string; email: string }>>([]);
  const [linkedItems, setLinkedItems] = useState<WorkItem[]>([]);
  
  // Integration with TransitionRulesManager - fetch applicable rules
  const [transitionRules, setTransitionRules] = useState<TransitionRule[]>([]);
  const [loadingRules, setLoadingRules] = useState(false);

  // Determine current stage
  const currentStage = useMemo(() => getWorkflowStage(sourceItem), [sourceItem]);
  const availableTransitions = useMemo(() => getAvailableTransitions(currentStage), [currentStage]);
  const stageConfig = WORKFLOW_STAGES[currentStage];
  
  // Load transition rules from TransitionRulesManager API
  const loadTransitionRules = useCallback(async () => {
    try {
      setLoadingRules(true);
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      // Fetch rules that apply to this item type
      const params = new URLSearchParams();
      if (sourceItem.item_type) {
        params.append('item_type', sourceItem.item_type);
      }
      params.append('from_status', sourceItem.status || '');

      const res = await fetch(`/api/v1/work-items/transitions?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data) {
          setTransitionRules(data.data);
        }
      }
    } catch (error) {
      console.error('Error loading transition rules:', error);
    } finally {
      setLoadingRules(false);
    }
  }, [sourceItem.item_type, sourceItem.status]);

  // Load rules on mount and when status changes
  useEffect(() => {
    loadTransitionRules();
  }, [loadTransitionRules]);

  // Check if a transition is allowed by rules
  const isTransitionAllowed = useCallback((toStatus: string): { allowed: boolean; reason?: string; requiredFields?: string[] } => {
    // If no rules defined, allow all default transitions
    if (transitionRules.length === 0) {
      return { allowed: true };
    }

    // Find applicable rule
    const rule = transitionRules.find(r => 
      r.from_status === sourceItem.status && 
      r.to_status === toStatus &&
      r.is_active &&
      (!r.item_type || r.item_type === sourceItem.item_type)
    );

    if (!rule) {
      // No rule defined for this transition - check if ANY rule exists for this from_status
      const hasAnyRuleFromStatus = transitionRules.some(r => r.from_status === sourceItem.status);
      if (hasAnyRuleFromStatus) {
        return { allowed: false, reason: 'This status transition is not allowed' };
      }
      // No rules at all for this status, allow
      return { allowed: true };
    }

    // Check required fields
    const missingFields: string[] = [];
    if (rule.required_fields && rule.required_fields.length > 0) {
      for (const field of rule.required_fields) {
        const fieldName = typeof field === 'string' ? field : field.field;
        const value = (sourceItem as any)[fieldName] || sourceItem.custom_fields?.[fieldName];
        if (!value) {
          missingFields.push(fieldName);
        }
      }
    }

    if (missingFields.length > 0) {
      return { 
        allowed: false, 
        reason: `Missing required fields: ${missingFields.join(', ')}`,
        requiredFields: missingFields
      };
    }

    // Check conditions
    if (rule.conditions && rule.conditions.length > 0) {
      for (const condition of rule.conditions) {
        const condType = condition.type || condition;
        
        switch (condType) {
          case 'has_assignee':
            if (!sourceItem.assigned_to) {
              return { allowed: false, reason: 'An assignee is required' };
            }
            break;
          case 'has_due_date':
            if (!sourceItem.due_date) {
              return { allowed: false, reason: 'A due date is required' };
            }
            break;
          case 'all_subtasks_complete':
            // Would need to check subtasks - for now, allow
            break;
        }
      }
    }

    return { allowed: true };
  }, [transitionRules, sourceItem]);

  // Get allowed next statuses based on rules
  const allowedNextStatuses = useMemo(() => {
    const statuses: Array<{ status: string; label: string; rule?: TransitionRule }> = [];
    
    for (const rule of transitionRules) {
      if (rule.from_status === sourceItem.status && rule.is_active) {
        const check = isTransitionAllowed(rule.to_status);
        statuses.push({
          status: rule.to_status,
          label: rule.name,
          rule
        });
      }
    }
    
    return statuses;
  }, [transitionRules, sourceItem.status, isTransitionAllowed]);

  // Load technicians when schedule modal is shown
  const loadTechnicians = useCallback(async () => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const res = await fetch('/api/v1/module-access/users?module_key=total-operations&include_operations_role=true', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      if (data.success && data.data) {
        setTechnicians(data.data.map((u: any) => ({
          id: u.id,
          name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
          email: u.email
        })));
      }
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  }, []);

  // Load linked items (jobs, tickets, appointments related to this item)
  const loadLinkedItems = useCallback(async () => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const res = await fetch(`/api/v1/work-items/${sourceItem.id}/relationships`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data?.relationships) {
          const items: WorkItem[] = [];
          
          // Process outgoing relationships
          const outgoing = data.data.relationships.outgoing || {};
          Object.values(outgoing).forEach((rels: any) => {
            rels.forEach((rel: any) => {
              if (rel.item) items.push(rel.item);
            });
          });
          
          // Process incoming relationships
          const incoming = data.data.relationships.incoming || {};
          Object.values(incoming).forEach((rels: any) => {
            rels.forEach((rel: any) => {
              if (rel.item) items.push(rel.item);
            });
          });
          
          setLinkedItems(items);
        }
      }
    } catch (error) {
      console.error('Error loading linked items:', error);
    }
  }, [sourceItem.id]);

  // Handle ticket to job conversion
  const handleConvertToJob = async (jobData: any) => {
    setLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      // Create the job with ticket data
      const res = await fetch('/api/v1/work-items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'create',
          item_type: 'job',
          title: jobData.title,
          description: sourceItem.description || '',
          priority: jobData.priority,
          estimated_hours: jobData.estimated_hours,
          contact_id: sourceItem.contact_id,
          contact_name: sourceItem.contact_name,
          contact_email: sourceItem.contact_email,
          contact_phone: sourceItem.contact_phone,
          location_id: sourceItem.location_id,
          location_address: sourceItem.location_address,
          location_coordinates: sourceItem.location_coordinates,
          module_context: 'service-operations',
          status: 'draft',
          custom_fields: {
            ...sourceItem.custom_fields,
            source_ticket_id: sourceItem.id,
            source_ticket_number: sourceItem.item_number,
            linked_asset_ids: jobData.linked_assets?.map((a: any) => a.id) || []
          }
        })
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to create job');

      const newJob = data.data;

      // Link job to ticket if requested
      if (jobData.link_ticket) {
        await fetch('/api/v1/work-item-relationships', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            action: 'create',
            sourceItemId: newJob.id,
            targetItemId: sourceItem.id,
            relationshipType: 'created_from'
          })
        });
      }

      // Link job to selected assets
      if (jobData.linked_assets && jobData.linked_assets.length > 0) {
        for (const asset of jobData.linked_assets) {
          await fetch('/api/v1/work-item-relationships', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              action: 'create',
              sourceItemId: newJob.id,
              targetItemId: asset.id,
              relationshipType: 'services' // Job services Asset
            })
          });
        }
      }

      // Update ticket status to indicate job created
      await fetch(`/api/v1/work-items/${sourceItem.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'in_progress',
          custom_fields: {
            ...sourceItem.custom_fields,
            linked_job_id: newJob.id,
            linked_job_number: newJob.item_number
          }
        })
      });

      const assetCount = jobData.linked_assets?.length || 0;
      notify.success(`Job #${newJob.item_number} created from ticket${assetCount > 0 ? ` with ${assetCount} asset${assetCount > 1 ? 's' : ''} linked` : ''}`);
      setShowConvertModal(false);
      onTransition?.(newJob, 'convertToJob');
    } catch (error: any) {
      notify.error(error.message || 'Failed to convert ticket to job');
    } finally {
      setLoading(false);
    }
  };

  // Handle scheduling an appointment
  const handleScheduleAppointment = async (appointmentData: any) => {
    setLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      // Calculate end time from start + duration
      const startDate = new Date(appointmentData.scheduled_start);
      const endDate = new Date(startDate.getTime() + appointmentData.estimated_hours * 60 * 60 * 1000);

      // Create the appointment
      const res = await fetch('/api/v1/work-items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'create',
          item_type: 'appointment',
          parent_id: sourceItem.id,
          title: `${appointmentData.appointment_type.replace(/_/g, ' ')} - ${sourceItem.title}`,
          description: `Appointment for job #${sourceItem.item_number}`,
          item_subtype: appointmentData.appointment_type,
          status: 'scheduled',
          scheduled_start: startDate.toISOString(),
          scheduled_end: endDate.toISOString(),
          estimated_hours: appointmentData.estimated_hours,
          assigned_to: appointmentData.assigned_to[0], // Primary assignee
          contact_id: sourceItem.contact_id,
          contact_name: sourceItem.contact_name,
          location_id: sourceItem.location_id,
          location_address: sourceItem.location_address,
          location_coordinates: sourceItem.location_coordinates,
          module_context: 'service-operations',
          custom_fields: {
            appointment_type: appointmentData.appointment_type,
            estimated_travel_minutes: appointmentData.include_travel_time ? appointmentData.estimated_travel_minutes : 0,
            notify_customer: appointmentData.notify_customer,
            all_assignees: appointmentData.assigned_to,
            parent_job_id: sourceItem.id,
            parent_job_number: sourceItem.item_number
          }
        })
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to create appointment');

      const newAppointment = data.data;

      // Create relationship: Appointment scheduled_for Job
      await fetch('/api/v1/work-item-relationships', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'create',
          sourceItemId: sourceItem.id,
          targetItemId: newAppointment.id,
          relationshipType: 'scheduled_for'
        })
      });

      // Update job status to scheduled
      await fetch(`/api/v1/work-items/${sourceItem.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'scheduled',
          scheduled_start: startDate.toISOString(),
          scheduled_end: endDate.toISOString(),
          assigned_to: appointmentData.assigned_to[0]
        })
      });

      notify.success(`Appointment scheduled for ${startDate.toLocaleDateString()}`);
      setShowScheduleModal(false);
      onTransition?.(newAppointment, 'scheduleAppointment');
    } catch (error: any) {
      notify.error(error.message || 'Failed to schedule appointment');
    } finally {
      setLoading(false);
    }
  };

  // Handle transition action
  const handleTransition = async (transition: WorkflowTransition) => {
    switch (transition.action) {
      case 'convertToJob':
        setShowConvertModal(true);
        break;
      case 'scheduleAppointment':
        await loadTechnicians();
        setShowScheduleModal(true);
        break;
      default:
        notify.info(`Action "${transition.label}" coming soon`);
    }
  };

  // Compact view for inline display
  if (compact) {
    return (
      <div className={`flex items-center gap-2 p-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className={`p-1.5 rounded ${stageConfig.bgColor}`}>
          <stageConfig.icon className={`w-4 h-4 ${stageConfig.color}`} />
        </div>
        <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          {stageConfig.label}
        </span>
        {availableTransitions.length > 0 && (
          <>
            <ArrowRight className="w-4 h-4 text-gray-400" />
            {availableTransitions.map((t, i) => (
              <button
                key={t.action}
                onClick={() => handleTransition(t)}
                className={`px-2 py-1 text-xs font-medium text-white rounded ${t.color} transition-colors`}
              >
                {t.label}
              </button>
            ))}
          </>
        )}
      </div>
    );
  }

  // Full view
  return (
    <div className={`rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${stageConfig.bgColor}`}>
            <stageConfig.icon className={`w-5 h-5 ${stageConfig.color}`} />
          </div>
          <div>
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Workflow: {stageConfig.label}
            </h3>
            <p className="text-sm text-gray-500">{stageConfig.description}</p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Workflow Progress */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4 overflow-x-auto pb-2">
          {Object.entries(WORKFLOW_STAGES).map(([key, stage], index) => {
            const isActive = key === currentStage;
            const isPast = Object.keys(WORKFLOW_STAGES).indexOf(key) < Object.keys(WORKFLOW_STAGES).indexOf(currentStage);
            
            return (
              <React.Fragment key={key}>
                <div className={`flex flex-col items-center min-w-max ${isActive ? 'scale-110' : ''} transition-transform`}>
                  <div className={`p-2 rounded-full ${
                    isActive ? stage.bgColor : isPast ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    {isPast ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <stage.icon className={`w-5 h-5 ${isActive ? stage.color : 'text-gray-400'}`} />
                    )}
                  </div>
                  <span className={`text-xs mt-1 ${isActive ? 'font-medium' : ''} ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {stage.label}
                  </span>
                </div>
                {index < Object.entries(WORKFLOW_STAGES).length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    isPast ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Available Actions */}
        {availableTransitions.length > 0 && (
          <div className="mt-4">
            <p className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Next Actions:
            </p>
            <div className="flex flex-wrap gap-2">
              {availableTransitions.map(t => (
                <button
                  key={t.action}
                  onClick={() => handleTransition(t)}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${t.color} transition-colors flex items-center gap-2`}
                >
                  <t.icon className="w-4 h-4" />
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Linked Items */}
        {linkedItems.length > 0 && (
          <div className="mt-4">
            <p className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Linked Items:
            </p>
            <div className="space-y-2">
              {linkedItems.map(item => (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}
                >
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    item.item_type === 'ticket' ? 'bg-blue-100 text-blue-800' :
                    item.item_type === 'job' ? 'bg-green-100 text-green-800' :
                    item.item_type === 'appointment' ? 'bg-purple-100 text-purple-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {item.item_type}
                  </span>
                  <span className="text-sm font-mono text-gray-500">#{item.item_number}</span>
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {item.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showConvertModal && (
        <ConvertToJobModal
          ticket={sourceItem}
          onConvert={handleConvertToJob}
          onCancel={() => setShowConvertModal(false)}
          loading={loading}
        />
      )}

      {showScheduleModal && (
        <ScheduleAppointmentModal
          job={sourceItem}
          technicians={technicians}
          onSchedule={handleScheduleAppointment}
          onCancel={() => setShowScheduleModal(false)}
          loading={loading}
        />
      )}
    </div>
  );
}

// ============================================================================
// QUICK ACTION BAR - Compact workflow actions for WorkItemDetailModal header
// ============================================================================

interface QuickWorkflowActionsProps {
  workItem: WorkItem;
  onStatusChange?: (newStatus: string) => void;
  onConvertToJob?: () => void;
  onCreateInvoice?: () => void;
}

export function QuickWorkflowActions({
  workItem,
  onStatusChange,
  onConvertToJob,
  onCreateInvoice
}: QuickWorkflowActionsProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  
  const currentStage = getWorkflowStage(workItem);
  const availableTransitions = getAvailableTransitions(currentStage);
  
  // Determine quick actions based on item type and status
  const quickActions = useMemo(() => {
    const actions: Array<{
      label: string;
      icon: React.ElementType;
      color: string;
      onClick: () => void;
      show: boolean;
    }> = [];
    
    // Ticket actions
    if (workItem.item_type === 'ticket') {
      if (workItem.status !== 'closed' && workItem.status !== 'resolved') {
        actions.push({
          label: 'Convert to Job',
          icon: Briefcase,
          color: 'bg-green-600 hover:bg-green-700',
          onClick: () => onConvertToJob?.(),
          show: true
        });
      }
    }
    
    // Email Task actions - similar to tickets, can be converted to jobs
    if (workItem.item_type === 'email_task') {
      if (workItem.status !== 'closed' && workItem.status !== 'completed' && workItem.status !== 'done') {
        actions.push({
          label: 'Convert to Job',
          icon: Briefcase,
          color: 'bg-green-600 hover:bg-green-700',
          onClick: () => onConvertToJob?.(),
          show: true
        });
      }
    }
    
    // Note: Invoice generation and status workflow actions removed from header
    // Invoice generation is now handled via the billing tab
    // Status changes are managed through the status dropdown in DetailsSidebar
    
    return actions.filter(a => a.show);
  }, [workItem, onConvertToJob]);
  
  if (quickActions.length === 0) return null;
  
  return (
    <div className="flex items-center gap-2">
      {quickActions.slice(0, 3).map((action, idx) => {
        const Icon = action.icon;
        return (
          <button
            key={idx}
            onClick={action.onClick}
            className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg ${action.color} transition-colors`}
            title={action.label}
          >
            <Icon className="w-3.5 h-3.5" />
            <span>{action.label}</span>
          </button>
        );
      })}
      {quickActions.length > 3 && (
        <span className="text-xs text-white/60">+{quickActions.length - 3} more</span>
      )}
    </div>
  );
}

// ============================================================================
// WORKFLOW STATUS BADGE - Shows current workflow stage
// ============================================================================

interface WorkflowStatusBadgeProps {
  workItem: WorkItem;
  showLabel?: boolean;
}

export function WorkflowStatusBadge({ workItem, showLabel = true }: WorkflowStatusBadgeProps) {
  const currentStage = getWorkflowStage(workItem);
  const stageConfig = WORKFLOW_STAGES[currentStage];
  
  if (!stageConfig) return null;
  
  const Icon = stageConfig.icon;
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full ${stageConfig.bgColor}`}>
      <Icon className={`w-3.5 h-3.5 ${stageConfig.color}`} />
      {showLabel && (
        <span className={`text-xs font-medium ${stageConfig.color}`}>
          {stageConfig.label}
        </span>
      )}
    </div>
  );
}